import { useEffect, useRef, useState } from "react";

export interface DownloadEstimate {
  seconds: number | null; // remaining download seconds, null if unknown
  bytesPerSec: number | null; // current rate, null if unknown
}

/**
 * Estimate the remaining download of the 3D room from the real byte stream.
 * Keeps a short rolling window of (time, loadedBytes) samples and extrapolates
 * the remaining bytes at the recent average rate, so the start screen's
 * "{rate} MB/s · About N sec to download" reflects the learner's actual
 * connection. Both values are null while unknown (no total, no measurable rate,
 * or already ready).
 */
export function useDownloadEstimate(loaded: number, total: number, ready: boolean): DownloadEstimate {
  const [val, setVal] = useState<DownloadEstimate>({ seconds: null, bytesPerSec: null });
  const samples = useRef<{ t: number; l: number }[]>([]);
  useEffect(() => {
    if (ready || total <= 0) {
      setVal({ seconds: null, bytesPerSec: null });
      return;
    }
    const s = samples.current;
    s.push({ t: Date.now(), l: loaded });
    while (s.length > 8) s.shift();
    if (s.length >= 2) {
      const a = s[0];
      const b = s[s.length - 1];
      const dl = b.l - a.l;
      const dt = (b.t - a.t) / 1000;
      if (dt > 0 && dl > 0) {
        const rate = dl / dt; // bytes/sec, averaged over the window
        setVal({ seconds: Math.max(0, total - loaded) / rate, bytesPerSec: rate });
      }
    }
  }, [loaded, total, ready]);
  return val;
}
